import { error } from 'common/errors';
import { pushErrors } from 'common/gamp';
import { saveMeta, saveProfile, saveUser } from 'communicator/store';
import {
  getApiKey,
  getDcfExperience,
  getFullAccessToken,
  getIntentPayload,
  getUser,
  getVIntentId
} from 'communicator/store/selectors';
import { fetchRevokeIdTokens } from 'communicator/utils/api/collection';
import { fetchPostIntent } from 'communicator/utils/api/intent';
import { fetchSpawnIdToken } from 'communicator/utils/api/spawn';
import getGenericError from 'communicator/utils/generic-error';
import { getLockoutFlowLegacy } from 'communicator/utils/get-lockout-flow';
import { getMaskedCardsFromPostIntent } from 'communicator/utils/get-masked-cards';
import convertIdTokenToAccessToken, { getIdToken } from 'communicator/utils/id-token';
import { markEnd, markStart } from 'communicator/utils/instrumentation';

type LegacyOptions = {
  idTokens: string[];
};

export default async function legacyPostIntentGetSrcProfile({ idTokens }: LegacyOptions) {
  try {
    const apikey = getApiKey();

    // If there is no accessToken present (i.e. user was recognized by other
    // networks but not Visa, or user hasn't completed identity validation), loop
    // through idTokens and call fetchToken until an accessToken is returned
    if (!getFullAccessToken()) {
      while (idTokens.length) {
        const idToken = idTokens.shift() as string;
        const tokenResponse = await convertIdTokenToAccessToken({
          idToken,
          marks: { token: 'gsp_id_to_access_token_LEGACY' }
        });

        if (tokenResponse.ok) {
          // Once an accessToken is receieved, continue to POST /intent
          break;
        } else if (getLockoutFlowLegacy(tokenResponse)) {
          saveUser({
            accessToken: null,
            isLocked: true
          });

          markEnd('GET_SRC_PROFILE_LEGACY', {
            doFlush: true,
            failure: true,
            reason: 'AUTH_INVALID'
          });

          return error('AUTH_INVALID');
        }

        // At this point we know that the /token API failed, but not due to
        // account lockout. Continue to try tokens until one works.
      }
    }

    // get latest accessToken
    const fullAccessToken = getFullAccessToken();

    // If no idTokens returned an accessToken, return auth error
    if (!fullAccessToken) {
      markEnd('GET_SRC_PROFILE_LEGACY', { doFlush: true, failure: true, reason: 'AUTH_INVALID' });
      return error('AUTH_INVALID');
    }

    // Revoke any remaining idTokens that didn't come from Visa
    const visaIdToken = getUser()?.idToken;
    const idTokensToRevoke = idTokens.filter(token => token !== visaIdToken);

    if (idTokensToRevoke.length) {
      // Fire and forget
      fetchRevokeIdTokens({
        accessToken: fullAccessToken,
        idTokens: idTokensToRevoke,
        mark: 'gsp_revoke_id_tokens_LEGACY'
      });
    }

    const intentPayload = getIntentPayload();
    const postIntentResponse = await fetchPostIntent({
      accessToken: fullAccessToken,
      apikey,
      intentPayload
    });

    if (!postIntentResponse.ok) {
      if (
        postIntentResponse.httpStatus === 400 ||
        postIntentResponse.errorCode === 'invalid_grant'
      ) {
        markEnd('GET_SRC_PROFILE_LEGACY', { doFlush: true, failure: true, reason: 'AUTH_INVALID' });
        return error('AUTH_INVALID');
      }

      const err = getGenericError(postIntentResponse);
      markEnd('GET_SRC_PROFILE_LEGACY', { doFlush: true, failure: true, reason: err });
      return error(err);
    }

    if (postIntentResponse.data.intentId) {
      saveMeta({
        /*
         * F27004 if digitalCardId present as well as id, create a mapping object
         * digitalCardId (vPanEnrollmentId) -> id (paymentInstrumentId) to convert
         * during checkout.
         */
        cardIdMapping:
          postIntentResponse.data.paymentInstrument?.reduce<Record<string, string>>((idMap, pi) => {
            if (pi.digitalCardId) {
              idMap[pi.digitalCardId] = pi.id;
            }

            return idMap;
          }, {}) ?? {},
        vIntentId: postIntentResponse.data.intentId
      });
    }

    // In integrated experiences we will need to parse out shippingAddressId for PUT intent
    // Also might need the selected card for rendering in step up flows
    const dcfExperience = getDcfExperience();

    if (dcfExperience === 'integrated') {
      saveProfile({
        shippingAddress: postIntentResponse.data.shippingAddress,
        paymentInstrument: postIntentResponse.data.paymentInstrument
      });
    }

    const srcCorrelationId = getVIntentId();
    const maskedCards = getMaskedCardsFromPostIntent(postIntentResponse.data);
    const maskedConsumer = postIntentResponse.data.consumer ?? {};

    // In legacy flows, we spawn a new idToken even if we already have one from
    // /recognize or /probe
    markStart('gsp_spawn_id_token_LEGACY');
    const spawnResponse = await fetchSpawnIdToken({
      accessToken: fullAccessToken,
      apikey
    });

    markEnd('gsp_spawn_id_token_LEGACY');

    if (spawnResponse.ok) {
      const idToken = getIdToken(spawnResponse.data.issued_tokens ?? []);

      if (idToken) {
        saveUser({ idToken });
      }

      markEnd('GET_SRC_PROFILE_LEGACY', { doFlush: true });
      return {
        profiles: [
          {
            idToken,
            maskedCards,
            maskedConsumer
          }
        ],
        srcCorrelationId
      };
    }

    markEnd('GET_SRC_PROFILE_LEGACY', { doFlush: true, failure: true, reason: 'UNKNOWN_ERROR' });
    // START GENAI@GHCOPILOT
    pushErrors('GET_SRC_PROFILE_LEGACY', 'UNKNOWN_ERROR');
    // END GENAI@GHCOPILOT
    return {
      profiles: [],
      srcCorrelationId
    };
  } catch (err: unknown) {
    // START GENAI@GHCOPILOT
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const sErr = JSON.stringify(err) !== undefined ? JSON.stringify(err) : 'Unknown error';
    pushErrors('GET_SRC_PROFILE_LEGACY', sErr);
    markEnd('GET_SRC_PROFILE_LEGACY', { doFlush: true, failure: true, reason: sErr });
    return error('GET_SRC_PROFILE_LEGACY_ERROR', sErr);
    // END GENAI@GHCOPILOT
  }
}
